import { Box, Button, Container, Divider, Title } from '@mantine/core';
import { joiResolver, useForm } from '@mantine/form';
import { showNotification } from '@mantine/notifications';
import React, { useState } from 'react';
import { Check, X } from 'tabler-icons-react';
import { BillingCycle } from '../../../../enums/entity.enums';
import { OpenComponent } from '../../../../enums/misc.enum';
import { Currency } from '../../../../enums/retrieve-transaction.enums';
import { Roles } from '../../../../enums/roles-enum';
import useAuth from '../../../../hooks/useAuth';
import useAxiosPrivate from '../../../../hooks/useAxiosPrivate';
import {
  CreateMerchantDetailsForm,
  CreateMerchantsRequest,
} from '../../../../type/entity.types';
import { ErrorResponse } from '../../../../type/error-response.types';
import BackButton from '../../../buttons/back-button.component';
import {
  AdminCreateMerchantSchema,
  CreateMerchantSchema,
  PartnerCreateMerchantSchema,
} from '../schemas/create-merchant.schema';
import MerchantDetails from './merchant-details.component';

function CreateMerchantEntity(props: Props) {
  const { setComponentManager, setRefreshTable } = props;
  const [isLoading, setIsLoading] = useState(false);
  const axiosPrivate = useAxiosPrivate();
  const { auth } = useAuth();

  const form = useForm<CreateMerchantDetailsForm>({
    initialValues: {
      partnerId: undefined,
      resellerId: undefined,
      entityName: '',
      email: '',

      firstName: '',
      middleName: '',
      noMiddleName: false,
      lastName: '',
      username: '',
      password: '',
      confirmPassword: '',

      billingCycleId: BillingCycle.WEEKLY,
      currency: Currency.PHP,
      pin: '',
    },
    validate: joiResolver(
      auth?.role === Roles.ADMINISTRATOR
        ? AdminCreateMerchantSchema
        : auth?.role === Roles.PARTNER
        ? PartnerCreateMerchantSchema
        : CreateMerchantSchema
    ),
  });

  const handleSubmit = async (e: any) => {
    const values = { ...form.values };

    const request: CreateMerchantsRequest = {
      partnerId: values.partnerId!,
      resellerId: values.resellerId!,

      merchantName: values.entityName,
      billingCycleId: values.billingCycleId,
      currency: values.currency,

      contact: {
        email: values.email,
      },

      user: {
        firstName: values.firstName,
        middleName: values.middleName ?? '.',
        lastName: values.lastName,
        username: values.username,
        password: values.password,
        pin: values.pin,
      },
    };

    try {
      setIsLoading(true);

      await axiosPrivate.post('web/merchants', request);

      showNotification({
        id: 'successfully-created-merchant',
        message: 'Successfully created merchant',
        icon: <Check size={16} />,
        color: 'teal',
        autoClose: 6000,
      });
      setRefreshTable((prev) => !prev);
    } catch (err: any) {
      const error: ErrorResponse = err.response?.data;

      showNotification({
        id: 'failed-to-create-merchant',
        message: error?.error || 'An Unexpected Error Occurred',
        icon: <X size={16} />,
        color: 'red',
        autoClose: 10000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Container fluid>
        <BackButton
          handleClick={() => {
            setComponentManager(OpenComponent.HOME);
          }}
        />
        <Title order={2} my='lg'>
          Merchant Registration
        </Title>
        <Divider my='xl' />
        <form onSubmit={form.onSubmit(handleSubmit)}>
          <MerchantDetails
            form={form}
            isAdmin={auth?.role === Roles.ADMINISTRATOR}
            isPartner={auth?.role === Roles.PARTNER}
          />
          <Box
            my='md'
            mt={30}
            style={{ display: 'flex', justifyContent: 'flex-end' }}
          >
            <Button type='submit' loading={isLoading}>
              Submit
            </Button>
          </Box>
        </form>
      </Container>
    </>
  );
}

export default CreateMerchantEntity;

interface Props {
  setComponentManager: React.Dispatch<React.SetStateAction<OpenComponent>>;
  setRefreshTable: React.Dispatch<React.SetStateAction<boolean>>;
}
